/**
 * Execute Agent Function — pick a function, supply JSON (+ optional file), run it.
 *
 * Layout follows CoordioCore form chrome: `ShellHeader` + `FormSection` cards with
 * `FormField` / `CoordioDropdown` (Site-style searchable select) and foundation buttons.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import Mustache from 'mustache';
import {
  Button as UiButton,
  Chip,
  CoordioDropdown,
  FormActions,
  FormField,
  ShellHeader,
} from '@coordio/ui';
import { FormSection, InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import {
  Play as ExecuteIcon,
  Paperclip as AttachIcon,
  Trash2 as RemoveIcon,
  Eye as PreviewIcon,
  EyeOff as HidePreviewIcon,
} from 'lucide-react';

import { CodeEditor } from '../components/common/CodeEditor';
import { agentFunctionService } from '../services/agentFunctionService';
import { useNotificationStore } from '../store/notificationStore';
import type { AgentFunctionDto, AgentFunctionRequestDto } from '../types/agentFunction';
import { AgentFunctionRequestStatus } from '../types/agentFunction';
import { ROUTES } from '../utils/routes';

import './AgentFunctionExecutePage.scss';

export const AgentFunctionExecutePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const slugFromUrl = searchParams.get('slug');
  const showNotification = useNotificationStore((s) => s.showNotification);

  const [functions, setFunctions] = useState<AgentFunctionDto[]>([]);
  const [selectedFunction, setSelectedFunction] = useState<AgentFunctionDto | null>(null);
  const [description, setDescription] = useState('');
  const [systemPromptTemplate, setSystemPromptTemplate] = useState('');
  const [inputJson, setInputJson] = useState('{}');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [loadingFunctions, setLoadingFunctions] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<AgentFunctionRequestDto | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const load = async () => {
      setLoadingFunctions(true);
      try {
        const res = await agentFunctionService.getAgentFunctions({
          isActive: true,
          pageSize: 100,
        });
        setFunctions(res.items);
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Failed to load agent functions';
        useNotificationStore.getState().showNotification(msg, 'error');
      } finally {
        setLoadingFunctions(false);
      }
    };
    void load();
  }, []);

  useEffect(() => {
    if (!slugFromUrl || functions.length === 0) return;
    const match = functions.find((f) => f.slug === slugFromUrl);
    if (match) setSelectedFunction(match);
  }, [slugFromUrl, functions]);

  useEffect(() => {
    if (!selectedFunction) {
      setDescription('');
      setSystemPromptTemplate('');
      setInputJson('{}');
      setResult(null);
      return;
    }

    const load = async () => {
      try {
        const detail = await agentFunctionService.getAgentFunctionBySlug(selectedFunction.slug);
        setDescription(detail.description ?? '');
        setSystemPromptTemplate(detail.systemPrompt ?? '');
        const template = detail.inputSchemaJson?.trim() || '{}';
        try {
          const parsed = JSON.parse(template);
          setInputJson(JSON.stringify(parsed, null, 2));
        } catch {
          setInputJson(template);
        }
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Failed to load function details';
        useNotificationStore.getState().showNotification(msg, 'error');
      }
    };

    void load();
    setResult(null);
  }, [selectedFunction]);

  const functionOptions = useMemo(
    () =>
      functions.map((f) => ({
        value: f.slug,
        label: f.name,
        description: f.slug,
      })),
    [functions],
  );

  const handleFunctionChange = useCallback(
    (slug: string | null) => {
      const match = slug ? (functions.find((f) => f.slug === slug) ?? null) : null;
      setSelectedFunction(match);
      if (slug) {
        setSearchParams({ slug });
      } else {
        const next = new URLSearchParams(searchParams);
        next.delete('slug');
        setSearchParams(next);
      }
    },
    [functions, searchParams, setSearchParams],
  );

  const assignFile = useCallback((file: File | null) => {
    setAttachedFile(file);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    assignFile(e.target.files?.[0] ?? null);
  };

  const handleRemoveFile = () => {
    assignFile(null);
    setFileInputKey((k) => k + 1);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) assignFile(file);
  };

  const handleExecute = async () => {
    if (!selectedFunction) return;

    setExecuting(true);
    setResult(null);
    try {
      const res = await agentFunctionService.executeAgentFunction(
        selectedFunction.slug,
        inputJson,
        attachedFile ?? undefined,
      );
      setResult(res);
      showNotification('Function executed successfully', 'success');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Execution failed';
      showNotification(msg, 'error');
      setResult({
        id: '',
        agentFunctionId: selectedFunction.id,
        agentFunctionName: selectedFunction.name,
        agentFunctionSlug: selectedFunction.slug,
        inputJson,
        status: AgentFunctionRequestStatus.Failed,
        errorMessage: msg,
        createdAt: new Date().toISOString(),
      });
    } finally {
      setExecuting(false);
    }
  };

  const resolvedPromptPreview = useMemo(() => {
    if (!systemPromptTemplate) return '';
    try {
      const inputData = JSON.parse(inputJson || '{}');
      return Mustache.render(systemPromptTemplate, inputData);
    } catch {
      return systemPromptTemplate;
    }
  }, [systemPromptTemplate, inputJson]);

  const hasMustacheVariables = useMemo(
    () => /\{\{[#/^]?[a-zA-Z_][a-zA-Z0-9_.]*\}\}/.test(systemPromptTemplate),
    [systemPromptTemplate],
  );

  const statusLabel =
    result?.status === AgentFunctionRequestStatus.Completed
      ? 'Completed'
      : result?.status === AgentFunctionRequestStatus.Failed
        ? 'Failed'
        : result?.status === AgentFunctionRequestStatus.Processing
          ? 'Processing'
          : 'Pending';
  const statusColor =
    result?.status === AgentFunctionRequestStatus.Completed
      ? 'green'
      : result?.status === AgentFunctionRequestStatus.Failed
        ? 'red'
        : 'default';

  return (
    <div className="agent-function-execute" data-testid="agent-function-execute-page">
      <ShellHeader
        title="Execute Agent Function"
        description="Select an agent function, provide input, and execute."
        data-testid="agent-function-execute-header"
      />

      <PageContainer data-testid="agent-function-execute-form-page">
        <div className="agent-function-execute__stack">
          <FormSection
            title="Agent Function"
            description="Choose which registered function to run."
            data-testid="agent-function-execute-section-agent-function"
          >
            {loadingFunctions ? (
              <LoadingSkeleton rows={2} data-testid="agent-function-execute-loading" />
            ) : (
              <FormField label="Function" required>
                <CoordioDropdown
                  options={functionOptions}
                  value={selectedFunction?.slug ?? null}
                  onChange={(v) => handleFunctionChange(v)}
                  placeholder="Select function by name or slug"
                  searchPlaceholder="Search functions…"
                  searchable
                  clearable
                  optionTemplate={{ preset: 'detail' }}
                  data-testid="agent-function-execute-function-selector"
                />
              </FormField>
            )}
            {description ? (
              <p className="agent-function-execute__description">{description}</p>
            ) : null}
          </FormSection>

          <FormSection title="Input JSON" data-testid="agent-function-execute-section-input-json">
            <FormField
              label="Payload"
              description="JSON body sent to the function. Schema is prefilled from the function definition when available."
            >
              <div className="agent-function-execute__editor">
                <CodeEditor
                  value={inputJson}
                  onChange={setInputJson}
                  language="json"
                  height="240px"
                  readOnly={false}
                />
              </div>
            </FormField>
          </FormSection>

          {systemPromptTemplate && hasMustacheVariables ? (
            <FormSection
              title="Resolved System Prompt Preview"
              data-testid="agent-function-execute-section-resolved-prompt-preview"
            >
              <div className="agent-function-execute__preview-toolbar">
                <UiButton
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => setShowPreview((v) => !v)}
                  data-testid="agent-function-execute-preview-btn"
                >
                  {showPreview ? <HidePreviewIcon size={16} aria-hidden /> : <PreviewIcon size={16} aria-hidden />}
                  {showPreview ? 'Hide' : 'Show'} Preview
                </UiButton>
              </div>
              {showPreview ? (
                <FormField
                  label="Preview"
                  description="Mustache variables substituted with the current input JSON."
                >
                  <div className="agent-function-execute__editor">
                    <CodeEditor
                      value={resolvedPromptPreview}
                      language="plaintext"
                      height="180px"
                      readOnly
                    />
                  </div>
                </FormField>
              ) : null}
            </FormSection>
          ) : null}

          <FormSection
            title="File Attachment"
            description="Optional. Attach one file to send with the execution request."
            data-testid="agent-function-execute-section-file-attachment"
          >
            <input
              key={fileInputKey}
              ref={fileInputRef}
              type="file"
              id="agent-function-file"
              className="sr-only"
              onChange={handleFileChange}
              accept="*/*"
            />
            <div
              className={
                dragOver
                  ? 'agent-function-execute__dropzone agent-function-execute__dropzone--active'
                  : 'agent-function-execute__dropzone'
              }
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <UiButton
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => fileInputRef.current?.click()}
                data-testid="agent-function-execute-attach-btn"
              >
                <AttachIcon size={16} aria-hidden />
                Attach File
              </UiButton>

              {attachedFile ? (
                <div className="agent-function-execute__file-row">
                  <span className="agent-function-execute__file-name" title={attachedFile.name}>
                    {attachedFile.name}
                  </span>
                  <UiButton
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={handleRemoveFile}
                    className="agent-function-execute__file-remove"
                    aria-label="Remove file"
                    data-testid="agent-function-execute-remove-file-btn"
                  >
                    <RemoveIcon size={16} aria-hidden />
                  </UiButton>
                </div>
              ) : (
                <p className="agent-function-execute__file-hint">
                  No file selected — drag and drop or browse
                </p>
              )}
            </div>
          </FormSection>

          <FormActions>
            <UiButton
              type="button"
              variant="default"
              className="gap-2"
              onClick={() => void handleExecute()}
              disabled={!selectedFunction || executing}
              data-testid="agent-function-execute-execute-btn"
            >
              <ExecuteIcon size={16} aria-hidden />
              {executing ? 'Executing…' : 'Execute'}
            </UiButton>
          </FormActions>

          {result ? (
            <FormSection title="Result" data-testid="agent-function-execute-section-result">
              <div className="agent-function-execute__result-meta">
                <Chip label={statusLabel} color={statusColor} />
                {result.durationMs != null ? (
                  <span className="agent-function-execute__duration">{result.durationMs} ms</span>
                ) : null}
              </div>

              {result.status === AgentFunctionRequestStatus.Failed && result.errorMessage ? (
                <InlineErrorPanel
                  message={result.errorMessage}
                  data-testid="agent-function-execute-error-panel"
                />
              ) : null}

              {result.outputJson != null && result.outputJson !== '' ? (
                <FormField label="Output JSON">
                  <div className="agent-function-execute__editor">
                    <CodeEditor
                      value={result.outputJson ?? '{}'}
                      language="json"
                      height="300px"
                      readOnly
                    />
                  </div>
                </FormField>
              ) : null}

              {result.id ? (
                <RouterLink
                  to={ROUTES.AGENT_FUNCTION_REQUEST_DETAIL.replace(':requestId', result.id)}
                  className="agent-function-execute__detail-link"
                >
                  View full request detail
                </RouterLink>
              ) : null}
            </FormSection>
          ) : null}
        </div>
      </PageContainer>
    </div>
  );
};
